import type { FC } from "react";
import React, { useCallback, useState } from "react";
import { EmailInput, validateEmail } from "@components/Auth/Form/Email";
import ModalMessage from "@components/AuthFlow/Helpers/ModalMessage";
import SubmitButton, {
  submitButtonId,
} from "@components/AuthFlow/Passwordless/SubmitButton";
import {
  trackError,
  trackStarted,
  trackSuccess,
  withPrefix,
} from "@tracking/tracking";
import {
  useLandingPagePath,
  useSendAuthenticationEmail,
} from "clientState/AuthFlow/hooks";
import type { OnSuccessType } from "clientState/AuthFlow/hooks/SendAuthenticationEmail/contract";
import { useModalActions } from "clientState/AuthFlow/providers/ModalScreenProvider";
import {
  screenNames,
  useScreenState,
} from "clientState/AuthFlow/providers/ScreenProvider";
import { useTracking, useTranslation } from "@edreamsodigeo/frontend-core";
import { Box, Flex } from "@edreamsodigeo/prisma-design-system";

import type { PasswordlessFormProps } from "./contract";
import { CLICKED_ID } from "./datameshIds";

const { PASSWORDLESS } = screenNames;

const PasswordlessForm: FC<PasswordlessFormProps> = ({
  email,
  setEmail,
  acceptsNewsletter,
  submitButtonLabel,
  emailLabelForm,
  isLoginSyncEnabled,
  renderSuccessScreen,
  renderLoginSyncScreen,
}) => {
  const [emailErrors, setEmailErrors] = useState<string[]>([]);
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);

  const landingPagePath = useLandingPagePath();

  const { t } = useTranslation();
  const { sendAuthenticationEmail } = useSendAuthenticationEmail();
  const { trackEvent } = useTracking();
  const { state } = useScreenState();
  const gaTrackingPrefix = state.gaTrackingPrefix || state.trackingPrefix;
  const { updateAuthenticationStatus } = useModalActions();
  const successAuthEmail = useCallback<OnSuccessType>(
    ({ acknowledged, multiSessionToken }) => {
      setLoading(false);
      if (acknowledged) {
        trackSuccess({
          trackEvent,
          screen: PASSWORDLESS,
          prefix: gaTrackingPrefix,
        });
        if (isLoginSyncEnabled && multiSessionToken) {
          renderLoginSyncScreen({ email, multiSessionToken });
        } else {
          renderSuccessScreen({ email });
          updateAuthenticationStatus("email-sent");
        }
      } else {
        trackError({
          trackEvent,
          screen: PASSWORDLESS,
          prefix: gaTrackingPrefix,
        });
        setShowError(true);
      }
    },
    [
      email,
      isLoginSyncEnabled,
      renderLoginSyncScreen,
      renderSuccessScreen,
      gaTrackingPrefix,
      trackEvent,
      updateAuthenticationStatus,
    ],
  );

  const errorAuthEmail = useCallback(() => {
    trackError({
      trackEvent,
      screen: PASSWORDLESS,
      prefix: gaTrackingPrefix,
    });
    setShowError(true);
    setLoading(false);
  }, [gaTrackingPrefix, trackEvent]);

  const onEnterSubmit = (): void => {
    document.getElementById(submitButtonId)?.click();
  };

  const handleSubmit = useCallback(async () => {
    setShowError(false);
    setLoading(true);
    trackStarted({
      trackEvent,
      screen: PASSWORDLESS,
      prefix: gaTrackingPrefix,
    });
    const { value: isValidEmail, errors: emailErrors } = validateEmail(email);

    if (isValidEmail) {
      sendAuthenticationEmail({
        email,
        landingPagePath,
        newsletterSubscribed: acceptsNewsletter,
        onSuccess: successAuthEmail,
        onError: errorAuthEmail,
      });
    } else {
      setEmailErrors(emailErrors);
      trackError({
        trackEvent,
        screen: PASSWORDLESS,
        prefix: gaTrackingPrefix,
      });
      setLoading(false);
    }
  }, [
    trackEvent,
    gaTrackingPrefix,
    email,
    sendAuthenticationEmail,
    landingPagePath,
    acceptsNewsletter,
    successAuthEmail,
    errorAuthEmail,
  ]);

  return (
    <Flex flexDirection="column" gap="1x_small">
      {showError && (
        <ModalMessage
          type="ERROR"
          message={t("authentication.passwordless.error")}
        />
      )}
      <EmailInput
        id="id-email-input"
        email={email}
        setEmail={setEmail}
        errors={emailErrors}
        clearErrors={(): void => setEmailErrors([])}
        disabled={loading}
        label={emailLabelForm}
        onEnter={onEnterSubmit}
        trackingPrefix={state.trackingPrefix}
      />
      <Box mt="1x_small">
        <SubmitButton
          loading={loading}
          onClick={handleSubmit}
          label={submitButtonLabel}
          trackingId={withPrefix(CLICKED_ID, state.trackingPrefix)}
        />
      </Box>
    </Flex>
  );
};

export default PasswordlessForm;
